'use client';

import React from 'react';
import { twMerge } from 'tailwind-merge';

interface IconButtonProps extends React.HTMLAttributes<HTMLElement> {
  children: React.ReactNode;
  color?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success' | 'default';
  size?: 'small' | 'medium' | 'large';
  disabled?: boolean;
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
  className?: string;
  component?: React.ElementType;
  'aria-label'?: string;
  href?: string;
  target?: string;
  rel?: string;
}

export const IconButton: React.FC<IconButtonProps> = ({
  children,
  color = 'default',
  size = 'medium',
  disabled = false,
  onClick,
  className = '',
  component: Component,
  'aria-label': ariaLabel,
  ...other
}) => {
  // Base classes
  const baseClasses = twMerge(
    'inline-flex items-center justify-center rounded-full transition-colors duration-200',
    'border-none outline-none',
    disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
  );

  // Size classes
  const sizeClasses = {
    small: 'p-1.5 text-sm',
    medium: 'p-2 text-base',
    large: 'p-2.5 text-lg',
  }[size];

  // Color classes
  const colorClasses = {
    primary: 'text-blue-600 hover:bg-blue-100',
    secondary: 'text-purple-600 hover:bg-purple-100',
    error: 'text-red-600 hover:bg-red-100',
    warning: 'text-orange-600 hover:bg-orange-100',
    info: 'text-blue-500 hover:bg-blue-100',
    success: 'text-green-600 hover:bg-green-100',
    default: 'text-gray-600 hover:bg-gray-100',
  }[color];

  // Combine all classes
  const classes = twMerge(baseClasses, sizeClasses, colorClasses, className);

  // If a custom component is provided, use it
  if (Component) {
    return (
      <Component
        onClick={onClick}
        disabled={disabled}
        className={classes}
        aria-label={ariaLabel}
        {...other}
      >
        {children}
      </Component>
    );
  }

  // Otherwise, render a button
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={classes}
      aria-label={ariaLabel}
      type="button"
      {...other}
    >
      {children}
    </button>
  );
};

export default IconButton;
